import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { windowWrapper } from '@clubmed/utils';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useFormikContext } from 'formik';
import type { ReservationBlockFormValues } from 'Src/types/reservation';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { getAttendeeLabel } from 'Components/widgets/reservation-block/reservationBlock.utils';
import { REMAINING_STOCK_WARNING_THRESHOLD } from 'Components/widgets/activity-card/ActivityCard';
import { ServicesContext } from 'Context/ServicesContext';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { momentFromApiDate } from 'Utils/date/apiDate';
import { DateSchedule } from 'Components/widgets/reservation-block/DateSchedule';
type Props = {
  attendees: ReadonlyArray<AttendeeWithSchedulesType>;
};
export const ReservationScheduleStep = ({ attendees }: Props) => {
  const appContext = useAppContext();
  const {
    attendeesList,
    scheduleReservationMessage,
    showRentAllSchedules,
    departureDate,
    arrivalDate,
  } = useContext(ServicesContext);
  const { values, validateForm, setFieldValue, setValues } = useFormikContext<ReservationBlockFormValues>();
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const [lastAvailableSchedule, setLastAvailableSchedule] = useState({});

  const applyBatchedScheduleDaySelection = (
    scheduleIndex: number,
    checked: boolean,
    allSchedulesWillBeChecked: boolean,
  ) => {
    // Double rAF: run after field.onChange has committed so we don't overwrite with stale state.
    // @see https://github.com/jaredpalmer/formik/issues/2059
    windowWrapper.requestAnimationFrame(() => {
      windowWrapper.requestAnimationFrame(() => {
        const currentValues = valuesRef.current;
        setValues(
          {
            ...currentValues,
            attendees: Object.fromEntries(
              Object.entries(currentValues.attendees).map(([id, attendee]) => [
                id,
                {
                  ...(attendee as object),
                  schedules: (
                    attendee as { schedules: Array<{ isSelected: boolean }> }
                  ).schedules.map((schedule, idx) =>
                    idx === scheduleIndex ? { ...schedule, isSelected: checked } : schedule,
                  ),
                },
              ]),
            ),
            wholeDurationOfStay: Object.fromEntries(
              Object.keys(currentValues.wholeDurationOfStay).map((id) => [
                id,
                allSchedulesWillBeChecked,
              ]),
            ),
          },
          true,
        );
      });
    });
  };
  // find the all duration schedule if exists
  // he has a special treatment because of the childcare BY DAY
  // that is equivalent to the whole duration of stay
  useEffect(() => {
    validateForm();
    Object.entries(values.attendees)
      .filter(([, { selected }]) => !selected)
      .forEach(([attendeeId]) => {
        const attendeeSchedules = values.attendees[attendeeId].schedules;
        attendeeSchedules.forEach(({ isSelected }, scheduleIndex) => {
          if (!isSelected) {
            return;
          }

          setFieldValue(`attendees.${attendeeId}.schedules[${scheduleIndex}].isSelected`, false);
        });
      });
    return () => {
      // when the component is unmount the validation is not triggered and the confirm button can stay disabled
      validateForm();
    };
  }, []);
  const hasPaidSchedules = attendees.some((attendee) =>
    attendee.schedules.some(({ timeSlots }) => timeSlots.some(({ isPaid }) => isPaid)),
  );

  const checkAllSchedules = ({ target }) => {
    if (!target.checked) {
      // field.onChange already sets sameScheduleForAll to false.
      // Do not call setValues here: spreading stale values would reset the checkbox to checked.
      return;
    }

    windowWrapper.requestAnimationFrame(() => {
      const currentValues = valuesRef.current;
      const sourceAttendeeSchedules = currentValues.attendees.A?.schedules;
      const sourceWholeDuration = currentValues.wholeDurationOfStay.A;

      if (!sourceAttendeeSchedules) {
        return;
      }

      const updatedAttendees = Object.fromEntries(
        Object.entries(currentValues.attendees).map(([attendeeId, attendee]) => {
          if (attendeeId === 'A') {
            return [attendeeId, attendee];
          }

          const updatedSchedules = (
            attendee as { schedules: Array<{ isSelected: boolean }> }
          ).schedules.map((schedule, scheduleIndex) => ({
            ...schedule,
            isSelected: sourceAttendeeSchedules[scheduleIndex]?.isSelected ?? false,
          }));

          return [attendeeId, { ...(attendee as object), schedules: updatedSchedules }];
        }),
      );

      const updatedWholeDurationOfStay = Object.fromEntries(
        Object.entries(currentValues.wholeDurationOfStay).map(([attendeeId]) => [
          attendeeId,
          attendeeId === 'A' ? currentValues.wholeDurationOfStay[attendeeId] : sourceWholeDuration,
        ]),
      );

      setValues(
        {
          ...currentValues,
          sameScheduleForAll: true,
          attendees: updatedAttendees,
          wholeDurationOfStay: updatedWholeDurationOfStay,
        },
        true,
      );
    });
  };

  return (
    <Fragment>
      {scheduleReservationMessage && (
        <p className="mt-10 text-12 font-bold text-sienna">{scheduleReservationMessage}</p>
      )}

      {!hasPaidSchedules &&
        Object.values(values.attendees).filter(({ selected }) => selected).length > 1 && (
          <div className="border-b-1 border-lightGrey py-15">
            <FormikInput
              label={t('booking.activities.skiEquipment.sameScheduleAllParticipants')}
              name="sameScheduleForAll"
              onChange={checkAllSchedules}
              type="checkbox"
            />
          </div>
        )}

      {Object.entries(values.attendees).map(([attendeeId, { schedules, selected }]) => {
        if (!selected) {
          return null;
        }

        const fullStayScheduleIndex = schedules.findIndex(
          ({ startDate, endDate }) => startDate === arrivalDate && endDate === departureDate,
        );
        const fullStaySchedule = schedules[fullStayScheduleIndex];
        const isThereAFullStaySchedule = fullStayScheduleIndex !== -1;
        const shouldActivateFullStayScheduleMode = isThereAFullStaySchedule && schedules.length > 1;
        const attendeeSelectedSchedules = schedules.filter(({ isSelected }) => isSelected);
        return (
          <div className="divide-y divide-lightGrey" key={attendeeId}>
            <p className="air360-replay-ignore flex items-center gap-5 py-10 text-14" data-cs-mask>
              <Icon name={ICONS.PEOPLE} width="1em" />
              {getAttendeeLabel(attendeesList, attendeeId, appContext)}
            </p>
            {showRentAllSchedules && (
              <FormikInput
                className="py-10"
                label={t('booking.activities.skiEquipment.wholeDurationOfStay')}
                name={`wholeDurationOfStay.${attendeeId}`}
                onChange={({ target }) => {
                  schedules.forEach((_, scheduleIndex) => {
                    windowWrapper.requestAnimationFrame(() => {
                      if (!values.attendees[attendeeId].schedules[scheduleIndex].isPaid) {
                        setFieldValue(
                          `attendees.${attendeeId}.schedules[${scheduleIndex}].isSelected`,
                          target.checked,
                        );
                      }
                    });

                    if (!values.sameScheduleForAll) {
                      return;
                    }

                    Object.keys(values.attendees).forEach((otherAttendeeId) => {
                      if (otherAttendeeId === attendeeId) {
                        return null;
                      }

                      windowWrapper.requestAnimationFrame(() => {
                        setFieldValue(
                          `attendees.${otherAttendeeId}.schedules[${scheduleIndex}].isSelected`,
                          target.checked,
                        );
                        setFieldValue(`wholeDurationOfStay.${otherAttendeeId}`, target.checked);
                      });
                    });
                  });
                }}
                type="checkbox"
              />
            )}
            <div className="flex flex-wrap gap-10 py-10">
              {schedules.map(({ endDate, startDate }, scheduleIndex) => {
                const overlap = attendeeSelectedSchedules.some(
                  (attendeeSelectedSchedule) =>
                    (attendeeSelectedSchedule.startDate !== startDate ||
                      attendeeSelectedSchedule.endDate !== endDate) &&
                    [
                      momentFromApiDate(attendeeSelectedSchedule.startDate),
                      momentFromApiDate(attendeeSelectedSchedule.endDate),
                    ].every(
                      (date) =>
                        date.isBetween(momentFromApiDate(startDate), momentFromApiDate(endDate)) ||
                        date.isSame(momentFromApiDate(startDate)) ||
                        date.isSame(momentFromApiDate(endDate)),
                    ),
                );
                // Disabling schedules frontend
                const currentChildSchedule = values.attendees[attendeeId].schedules[scheduleIndex];
                const fullStayScheduleIsPaid = fullStaySchedule?.isPaid; // TODO TODO : check if this is the right way to do it

                const isDisabledRegularCase = currentChildSchedule.isPaid || overlap;

                const isSingleDaySchedule = (schedule) => schedule.startDate === schedule.endDate;

                const isCurrentSingleAndFullStayPaid =
                  isSingleDaySchedule(currentChildSchedule) &&
                  currentChildSchedule.isPaid &&
                  fullStayScheduleIsPaid;
                const isCurrentFullStayAndOneSinglePaid =
                  !isSingleDaySchedule(currentChildSchedule) &&
                  schedules.some((schedule) => isSingleDaySchedule(schedule) && schedule.isPaid);
                const isDisableInFSSM = // if the current schedule if it's a single schedule & full stay schedule is paid
                  isCurrentSingleAndFullStayPaid || // if the current schedule is a full stay schedule & one of the single schedules is paid
                  isCurrentFullStayAndOneSinglePaid;
                const isDisabled = shouldActivateFullStayScheduleMode
                  ? isDisableInFSSM
                  : isDisabledRegularCase;
                return (
                  <FormikInput
                    isDisabled={isDisabled}
                    key={`${startDate}${endDate}`}
                    name={`attendees.${attendeeId}.schedules[${scheduleIndex}].isSelected`}
                    type="checkbox"
                    onChange={(e) => {
                      if (shouldActivateFullStayScheduleMode) {
                        // if you select a single day schedule, the full stay schedule is automatically unselected
                        if (e.target.checked) {
                          const hasSelectedAllSingleSchedules =
                            e.target.checked &&
                            attendeeSelectedSchedules.length === schedules.length - 2;

                          // if you select all single day schedules, the full stay schedule is automatically selected
                          if (hasSelectedAllSingleSchedules) {
                            windowWrapper.requestAnimationFrame(() => {
                              values.attendees[attendeeId].schedules.forEach(
                                ({ startDate: startD, endDate: endD }, scheduleIndexFE) => {
                                  if (
                                    startD === fullStaySchedule.startDate &&
                                    endD === fullStaySchedule.endDate
                                  ) {
                                    setFieldValue(
                                      `attendees.${attendeeId}.schedules[${scheduleIndexFE}].isSelected`,
                                      true,
                                    );
                                  } else {
                                    setFieldValue(
                                      `attendees.${attendeeId}.schedules[${scheduleIndexFE}].isSelected`,
                                      false,
                                    );
                                  }
                                },
                              );
                            });
                          }
                        }

                        // if you unselect the first single day schedule, the full stay schedule is automatically unselected
                        if (
                          !e.target.checked &&
                          attendeeSelectedSchedules.length === schedules.length
                        ) {
                          windowWrapper.requestAnimationFrame(() => {
                            values.attendees[attendeeId].schedules.forEach(
                              ({ startDate: sDate, endDate: eDate }, scheduleIndexFE) => {
                                if (
                                  sDate === fullStaySchedule.startDate &&
                                  eDate === fullStaySchedule.endDate
                                ) {
                                  setFieldValue(
                                    `attendees.${attendeeId}.schedules[${scheduleIndexFE}].isSelected`,
                                    false,
                                  );
                                }
                              },
                            );
                          });
                        }

                        // if you select any single day schedule, the full stay schedule is automatically deselected
                        if (e.target.checked && attendeeSelectedSchedules.length === 1) {
                          windowWrapper.requestAnimationFrame(() => {
                            values.attendees[attendeeId].schedules.forEach(
                              ({ startDate: sd, endDate: ed }, scheduleIndexFE) => {
                                if (
                                  sd === fullStaySchedule.startDate &&
                                  ed === fullStaySchedule.endDate
                                ) {
                                  setFieldValue(
                                    `attendees.${attendeeId}.schedules[${scheduleIndexFE}].isSelected`,
                                    false,
                                  );
                                }
                              },
                            );
                          });
                        }
                      }

                      if (
                        !values.attendees[attendeeId].schedules[scheduleIndex].isSelected &&
                        values.attendees[attendeeId].schedules[scheduleIndex].remainingStock <=
                          REMAINING_STOCK_WARNING_THRESHOLD
                      ) {
                        setLastAvailableSchedule({
                          isVisible: true,
                          nb: values.attendees[attendeeId].schedules[scheduleIndex].remainingStock,
                        });
                      } else {
                        setLastAvailableSchedule({});
                      }

                      const allSchedulesWillBeChecked =
                        values.attendees[attendeeId].schedules.filter(
                          ({ isSelected }) => isSelected,
                        ).length ===
                          schedules.length - 1 && e.target.checked;
                      // hack to fix weird formik bug when calling setFieldValue in onChange
                      // @see https://github.com/jaredpalmer/formik/issues/2059
                      windowWrapper.requestAnimationFrame(() => {
                        setFieldValue(
                          `wholeDurationOfStay.${attendeeId}`,
                          allSchedulesWillBeChecked,
                        );
                      });

                      if (!values.sameScheduleForAll) {
                        return;
                      }

                      Object.keys(values.attendees).forEach((otherAttendeeId) => {
                        if (otherAttendeeId === attendeeId) {
                          return null;
                        }

                        windowWrapper.requestAnimationFrame(() => {
                          setFieldValue(
                            `attendees.${otherAttendeeId}.schedules[${scheduleIndex}].isSelected`,
                            e.target.checked,
                          );
                          setFieldValue(
                            `wholeDurationOfStay.${otherAttendeeId}`,
                            allSchedulesWillBeChecked,
                          );
                        });
                      });
                    }} // TODO: cleanup !!
                    // eslint-disable-next-line react/no-unstable-nested-components
                    placeholderComponent={(isChecked) => (
                      <div
                        className={classnames(
                          'flex items-center rounded-12 border-1 px-10 py-5 text-10 font-bold',
                          {
                            'border-lightGrey text-black': !isChecked,
                            'border-saffron-active bg-saffron text-black': isChecked,
                            'italic opacity-50': isDisabled,
                          },
                        )}
                        data-testid="reservationScheduleDay"
                      >
                        <DateSchedule date={startDate} />
                        {startDate !== endDate && (
                          <Fragment>
                            <div className="mx-10">{t('common.to')}</div>
                            <DateSchedule date={endDate} />
                          </Fragment>
                        )}
                      </div>
                    )}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      {lastAvailableSchedule.isVisible && (
        <p className="mt-5 flex items-center gap-5 text-14 font-bold leading-1 text-red">
          <Icon className="shrink-0" name={ICONS.WARNING} width="18px" />
          {t('booking.activities.remainingService', {
            nb: lastAvailableSchedule.nb,
          })}
        </p>
      )}
    </Fragment>
  );
};
